import { useMutation } from '@tanstack/react-query';
import { Col, Row } from 'antd';
import { ReportApis } from 'apis/ReportApis';
import ButtonExport from 'components/Button/ButtonExport';
import { useTitle } from 'contexts/TitleProvider';
import { useUser } from 'contexts/UserProvider';
import dayjs from 'dayjs';
import usePagination from 'hooks/usePagination';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { exportExcel_v2 } from 'utils/constants/exportExcel';
import FilterCustomerRevenue from './FilterCustomerRevenue';
import TableCustomerRevenue from './TableCustomerRevenue';
import { generateUUID } from 'utils/constants/functions';

const CustomerRevenuePage = () => {
  const { user } = useUser();
  const { setTitle, setBreadcrumb } = useTitle();
  const [data, setData] = useState<any>({});
  const [isShow, setIsShow] = useState(true);
  const [filters, setFilters] = useState<any>({
    plantId: 'all',
  });
  const { page, page_size } = usePagination();
  const navigate = useNavigate();
  const ref = useRef<any>(null);

  const defaultFilter = useMemo(() => {
    const userAny = user as any;

    if (user?.isAdmin)
      return {
        plantId: 'all',
        year: dayjs(),
      };

    if (user?.isSale) {
      const teamSale = userAny?.teamSales?.[0];

      return {
        year: dayjs(),
        plantId: userAny?.plantCode,
        teamSalesId: teamSale?.id || 'SALE_ID',
        teamSalesSubId: teamSale?.subId || 'SALE_SUB_ID',
        teamSalesName: teamSale?.name,
      };
    }

    if (user?.isHO)
      return {
        year: dayjs(),
        plantId: 'all',
      };

    return {
      plantId: userAny?.plantCode,
      year: dayjs(),
    };
  }, [user]);

  const { mutate: getCustomerRevenue, isPending: getCustomerRevenueLoading } =
    useMutation({
      mutationFn: ReportApis.getCustomerRevenue,
      onSuccess: (data: any, variables: any) => {
        const result = data?.data?.getCustomerFinancialReportData || {};
        const items = (result.items || []).map((item: any) => ({
          ...item,
          id: generateUUID(),
        }));

        if (variables.type === 'export') {
          const items = (result.items || []).map((item: any) => ({
            ...item,
            data_month_info: item.data_month_info?.replace('Tháng ', ''),
          }));

          return exportExcel_v2(
            variables.headerRow,
            items,
            'Báo cáo - Doanh thu Khách hàng',
          );
        }

        setData({ ...result, items });
      },
      onError: (error) => {
        console.log(error);
      },
    });

  const handleFilter = (values: any) => {
    setIsShow(true);
    setFilters(values);
    navigate(`?page=1&page_size=${page_size}`);
  };

  const handleCancel = () => {
    setIsShow(false);
    setFilters({});
  };

  const exportExcel = (columns: any) => {
    getCustomerRevenue({
      skip: 0,
      take: data.pageInfo?.totalItems || 1000,
      ...filters,
      plantId: filters.plantId ? filters.plantId.replace('all', '') : undefined,
      year: dayjs(filters.year).year().toString(),
      customerId: filters.customerId,
      type: 'export',
      headerRow: columns,
    });
  };

  const handleExportExcel = () => {
    if (!ref.current) return;

    ref.current.handleExportExcel();
  };

  useEffect(() => {
    setTitle('Doanh thu Khách hàng');
    setBreadcrumb([{ title: 'Báo cáo' }, { title: 'Doanh thu Khách hàng' }]);
  }, [setBreadcrumb, setTitle]);

  useEffect(() => {
    if (!isShow) return;

    getCustomerRevenue({
      plantId: filters.plantId.replace('all', ''),
      skip: page_size * (page - 1),
      take: page_size,
      year: dayjs(filters.year).year().toString(),
      customerId: filters.customerId,
      mainCustomerId: filters.mainCustomerId,
    });
  }, [
    filters.mainCustomerId,
    filters.customerId,
    filters.plantId,
    filters.year,
    getCustomerRevenue,
    isShow,
    page,
    page_size,
  ]);

  return (
    <>
      <Row gutter={[8, 16]} align={'middle'} justify={'end'}>
        <Col>
          <ButtonExport disabled={!isShow} onClick={handleExportExcel} />
        </Col>
      </Row>
      <FilterCustomerRevenue
        onFilter={handleFilter}
        onCancel={handleCancel}
        defaultFilter={defaultFilter}
      />
      {isShow && (
        <TableCustomerRevenue
          ref={ref}
          exportExcel={exportExcel}
          loading={getCustomerRevenueLoading}
          data={data}
        />
      )}
    </>
  );
};

export default CustomerRevenuePage;
